import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Hourglass, Loader2, Eye, EyeOff, ShieldCheck, Smartphone, KeyRound } from "lucide-react";
import logo_raya_EN from "assets/images/landing/logo_raya_EN1.png";
import { useLocation, useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { loginUser, otpReq, otpVerify } from "../services/authService";
import { useDispatch } from "react-redux";
import { login } from "../store/authSlice";
import AnimatedBackground from "units/client/component/AnimatedBackground";
import { useTranslation } from "react-i18next";

/* ------------------------------------------------------------------ */
/*  Styles (animations + react-phone-input-2 theming, light & dark)    */
/*  Dark mode expects Tailwind's class strategy: <html class="dark">   */
/* ------------------------------------------------------------------ */
const loginStyles = `
@keyframes raya-spin { to { transform: rotate(360deg); } }
@keyframes raya-spin-rev { to { transform: rotate(-360deg); } }
@keyframes raya-card-in {
  from { opacity: 0; transform: translateY(18px) scale(.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes raya-shine {
  0%   { transform: translateX(120%) skewX(-20deg); }
  100% { transform: translateX(-220%) skewX(-20deg); }
}
@keyframes raya-gradient {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.raya-ring-a { animation: raya-spin 22s linear infinite; }
.raya-ring-b { animation: raya-spin-rev 34s linear infinite; }
.raya-card-in { animation: raya-card-in .7s cubic-bezier(.2,.8,.2,1) both; }
.raya-title {
  background-size: 200% 200%;
  animation: raya-gradient 8s ease infinite;
}
.raya-cta { background-size: 200% 200%; animation: raya-gradient 6s ease infinite; }
.raya-cta .raya-shine {
  position: absolute; inset: 0; width: 40%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent);
  animation: raya-shine 3.2s ease-in-out infinite;
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .raya-ring-a, .raya-ring-b,
  .raya-card-in, .raya-title, .raya-cta, .raya-cta .raya-shine { animation: none !important; }
}

/* ---------- PhoneInput ---------- */
.raya-phone { direction: ltr; text-align: left; }
.raya-phone .form-control {
  width: 100% !important;
  height: 3.25rem !important;
  direction: ltr !important;
  text-align: left !important;
  padding-left: 3.75rem !important;
  border-radius: .9rem !important;
  border: 1px solid rgba(28,71,71,.22) !important;
  background: rgba(255,255,255,.75) !important;
  color: #0f2e2e !important;
  font-size: 1rem !important;
  caret-color: #c9962b;
  transition: border-color .2s, box-shadow .2s, background .2s;
}
.raya-phone .form-control:focus {
  border-color: #e0ad3a !important;
  box-shadow: 0 0 0 4px rgba(224,173,58,.22) !important;
  background: #fff !important;
}
.raya-phone .flag-dropdown {
  background: transparent !important;
  border: none !important;
  border-radius: .9rem 0 0 .9rem !important;
}
.raya-phone .selected-flag {
  width: 3.4rem !important;
  padding-left: 1rem !important;
  border-radius: .9rem 0 0 .9rem !important;
  background: transparent !important;
  outline: none !important;
  box-shadow: none !important;
}
.raya-phone .selected-flag:hover,
.raya-phone .selected-flag:focus,
.raya-phone .selected-flag.open { background: rgba(28,71,71,.07) !important; }
.raya-phone .selected-flag .arrow { border-top-color: #1c4747 !important; }
.raya-phone .country-list {
  width: min(320px, 86vw) !important;
  direction: ltr;
  text-align: left;
  border-radius: 1rem !important;
  border: 1px solid rgba(28,71,71,.12) !important;
  box-shadow: 0 20px 40px -12px rgba(15,46,46,.35) !important;
  overflow-x: hidden;
}
.raya-phone .country-list .country { padding: 9px 12px !important; }
.raya-phone .country-list .country:hover { background: rgba(224,173,58,.16) !important; }
.raya-phone .country-list .country.highlight { background: rgba(28,71,71,.1) !important; }
.raya-phone .country-list .country-name { margin-left: 30px; margin-right: 6px; }
.raya-phone .country-list .search { padding: 10px 8px !important; }
.raya-phone .country-list .search-box {
  width: 94% !important;
  margin: 0 auto !important;
  display: block !important;
  border-radius: .7rem !important;
  border: 1px solid rgba(28,71,71,.2) !important;
  padding: 8px 10px !important;
  outline: none !important;
}

.dark .raya-phone .form-control {
  background: rgba(255,255,255,.06) !important;
  border-color: rgba(255,255,255,.16) !important;
  color: #f7f3e6 !important;
}
.dark .raya-phone .form-control:focus {
  background: rgba(255,255,255,.1) !important;
  border-color: #f2c14e !important;
  box-shadow: 0 0 0 4px rgba(242,193,78,.2) !important;
}
.dark .raya-phone .selected-flag:hover,
.dark .raya-phone .selected-flag:focus,
.dark .raya-phone .selected-flag.open { background: rgba(255,255,255,.08) !important; }
.dark .raya-phone .selected-flag .arrow { border-top-color: #f2c14e !important; }
.dark .raya-phone .country-list {
  background: #0d2f2f !important;
  color: #f7f3e6 !important;
  border-color: rgba(255,255,255,.12) !important;
}
.dark .raya-phone .country-list .country:hover { background: rgba(242,193,78,.16) !important; }
.dark .raya-phone .country-list .country.highlight { background: rgba(255,255,255,.1) !important; }
.dark .raya-phone .country-list .country .dial-code { color: #9bc7c0 !important; }
.dark .raya-phone .country-list .search { background: #0d2f2f !important; }
.dark .raya-phone .country-list .search-box {
  background: rgba(255,255,255,.07) !important;
  border-color: rgba(255,255,255,.18) !important;
  color: #fff !important;
}
  /* ---------- Autofill (انتخاب شماره‌ی پیشنهادی مرورگر) ---------- */
.raya-field:-webkit-autofill,
.raya-field:-webkit-autofill:hover,
.raya-field:-webkit-autofill:active,
.raya-phone .form-control:-webkit-autofill,
.raya-phone .form-control:-webkit-autofill:hover,
.raya-phone .form-control:-webkit-autofill:active {
  -webkit-text-fill-color: #0f2e2e;
  caret-color: #0f2e2e;
  -webkit-box-shadow: 0 0 0 1000px #f1f6f5 inset !important;
  box-shadow: 0 0 0 1000px #f1f6f5 inset !important;
  transition: background-color 9999s ease-in-out 0s;
}
.raya-field:-webkit-autofill:focus,
.raya-phone .form-control:-webkit-autofill:focus {
  -webkit-text-fill-color: #0f2e2e;
  -webkit-box-shadow: 0 0 0 1000px #fff inset, 0 0 0 4px rgba(224,173,58,.22) !important;
  box-shadow: 0 0 0 1000px #fff inset, 0 0 0 4px rgba(224,173,58,.22) !important;
}

.dark .raya-field:-webkit-autofill,
.dark .raya-field:-webkit-autofill:hover,
.dark .raya-field:-webkit-autofill:active,
.dark .raya-phone .form-control:-webkit-autofill,
.dark .raya-phone .form-control:-webkit-autofill:hover,
.dark .raya-phone .form-control:-webkit-autofill:active {
  -webkit-text-fill-color: #f7f3e6;
  caret-color: #f7f3e6;
  -webkit-box-shadow: 0 0 0 1000px #0f2b2a inset !important;
  box-shadow: 0 0 0 1000px #0f2b2a inset !important;
}
.dark .raya-field:-webkit-autofill:focus,
.dark .raya-phone .form-control:-webkit-autofill:focus {
  -webkit-text-fill-color: #f7f3e6;
  -webkit-box-shadow: 0 0 0 1000px #143634 inset, 0 0 0 4px rgba(242,193,78,.2) !important;
  box-shadow: 0 0 0 1000px #143634 inset, 0 0 0 4px rgba(242,193,78,.2) !important;
}

/* Firefox */
.raya-field:autofill,
.raya-phone .form-control:autofill {
  box-shadow: 0 0 0 1000px #f1f6f5 inset !important;
  -webkit-text-fill-color: #0f2e2e;
}
.dark .raya-field:autofill,
.dark .raya-phone .form-control:autofill {
  box-shadow: 0 0 0 1000px #0f2b2a inset !important;
  -webkit-text-fill-color: #f7f3e6;
}
`;

/* ------------------------------------------------------------------ */
/*  Shared class names                                                 */
/* ------------------------------------------------------------------ */
const labelCls = "block text-sm font-medium mb-1.5 text-[#1c4747] dark:text-teal-100/90";

const inputCls =
    "raya-field w-full h-[3.25rem] rounded-[.9rem] px-4 text-base outline-none transition-all duration-200 " +
    "w-full h-[3.25rem] rounded-[.9rem] px-4 text-base outline-none transition-all duration-200 " +
    "border border-[#1c4747]/20 bg-white/75 text-[#0f2e2e] placeholder:text-sm placeholder:text-[#1c4747]/45 " +
    "focus:bg-white focus:border-[#e0ad3a] focus:ring-4 focus:ring-[#e0ad3a]/25 " +
    "dark:!border-white/15 dark:!bg-white/[0.06] dark:!text-[#f7f3e6] dark:placeholder:!text-white/40 " +
    "dark:focus:!bg-white/10 dark:focus:!border-[#f2c14e] dark:focus:!ring-[#f2c14e]/20";

const errCls = "text-rose-600 dark:text-rose-300 text-xs sm:text-sm mt-1.5";

const pillBtnClasses =
    "inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full font-medium shadow-sm text-sm whitespace-nowrap";

const ctaCls =
    "raya-cta relative overflow-hidden w-full h-[3.25rem] rounded-[.9rem] text-white font-semibold " +
    "bg-gradient-to-r from-[#1c4747] via-[#2a7a76] to-[#1c4747] " +
    "shadow-[0_10px_24px_-8px_rgba(28,71,71,.65)] hover:shadow-[0_14px_30px_-8px_rgba(28,71,71,.8)] " +
    "hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 " +
    "flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#e0ad3a]/50 " +
    "dark:from-[#e0ad3a] dark:via-[#f6d070] dark:to-[#e0ad3a] dark:text-[#0d2f2f] " +
    "dark:shadow-[0_10px_24px_-8px_rgba(242,193,78,.55)]";

const linkBtnCls =
    "mt-1 w-full text-center text-sm font-bold text-[#b8860b] hover:text-[#8a6508] " +
    "dark:text-[#f2c14e] dark:hover:text-[#ffe08a] transition-colors py-1.5";

export default function LoginPage() {
    const { t } = useTranslation(["user"]);

    const [sendingOtp, setSendingOtp] = useState(false);
    const sendingOtpRef = useRef(false);
    const [activeTab, setActiveTab] = useState("otp"); // 'password' | 'otp'
    const [loading, setLoading] = useState(false);
    const [otpTimer, setOtpTimer] = useState(0);
    const [autoOtpUsed, setAutoOtpUsed] = useState(false);
    const abortControllerRef = useRef(null);
    const [showPassword, setShowPassword] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [passwordTabValues, setPasswordTabValues] = useState({ username: "", password: "" });
    const [otpTabValues, setOtpTabValues] = useState({ mobile: "", otp: "" });
    const [isForgetPassword, setIsForgetPassword] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const isRegisterPage = location.pathname == "/register";
    const dispatch = useDispatch();

    function tryParsePhone(raw) {
        if (!raw) return null;
        const trimmed = String(raw).trim();
        const withPlus = trimmed.startsWith("+") ? trimmed : `+${trimmed}`;

        try {
            const pn = parsePhoneNumberFromString(withPlus);
            if (!pn || !pn.isValid()) return null;

            const bannedCountries = ["TR", "AF", "US"];
            if (bannedCountries.includes(pn.country)) return null;

            const type = pn.getType?.();
            if (type && type !== "MOBILE") return null;

            return pn;
        } catch {
            return null;
        }
    }

    const schema = (tab) =>
        Yup.object().shape({
            username:
                tab === "password"
                    ? Yup.string().required(t("login.errors.usernameRequired"))
                    : Yup.string().nullable(),

            password:
                tab === "password"
                    ? Yup.string().required(t("login.errors.passwordRequired"))
                    : Yup.string().nullable(),

            mobile:
                tab === "otp"
                    ? Yup.string()
                        .required(t("login.errors.mobileRequired"))
                        .test("is-valid-phone", t("login.errors.mobileInvalid"), (val) => {
                            if (!val || val.trim() === "" || val === "+98") return false;
                            try {
                                const withPlus = val.startsWith("+") ? val : `+${val}`;
                                const pn = parsePhoneNumberFromString(withPlus);
                                if (!pn || !pn.isValid()) return false;

                                const bannedCountries = ["TR", "AF", "US"];
                                if (bannedCountries.includes(pn.country)) return false;

                                const type = pn.getType?.();
                                if (type && type !== "MOBILE") return false;

                                return true;
                            } catch {
                                return false;
                            }
                        })
                    : Yup.string().nullable(),

            otp:
                tab === "otp"
                    ? Yup.string().required(t("login.errors.otpRequired"))
                    : Yup.string().nullable(),
        });

    const {
        register,
        handleSubmit,
        formState: { errors, touchedFields, isSubmitted },
        watch,
        setValue,
        reset,
        trigger,
        getValues,
        setFocus,
        clearErrors,
    } = useForm({
        mode: "onTouched",
        resolver: yupResolver(schema(activeTab)),
        defaultValues: {
            username: "",
            password: "",
            mobile: "+98",
            otp: "",
        },
    });

    const watchMobile = watch("mobile");

    useEffect(() => {
        let timer;
        if (otpTimer > 0) timer = setTimeout(() => setOtpTimer((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [otpTimer]);

    const tryWebOtp = async () => {
        try {
            if (!("OTPCredential" in window) && !navigator.credentials?.get) return false;
            abortControllerRef.current = new AbortController();
            const cred = await navigator.credentials.get({
                otp: { transport: ["sms"] },
                signal: abortControllerRef.current.signal,
            });
            if (cred?.code && /^\d{4,5}$/.test(cred.code.trim())) {
                setValue("otp", cred.code.trim(), { shouldValidate: true, shouldDirty: true });
                setAutoOtpUsed(true);
                toast.success(t("login.toasts.otpAutoFilled"), { containerId: "main-toast" });
                return true;
            }
        } catch { }
        return false;
    };

    useEffect(() => {
        if (abortControllerRef.current) {
            try {
                abortControllerRef.current.abort();
            } catch { }
            abortControllerRef.current = null;
        }
        if (activeTab === "otp" && window.innerWidth < 768) {
            const t = setTimeout(() => tryWebOtp(), 900);
            return () => clearTimeout(t);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleSendOtp = async () => {
        if (sendingOtpRef.current) return;

        const vals = getValues();
        const raw = vals.mobile?.trim();

        if (!raw) {
            toast.error(t("login.toasts.enterMobile"), { containerId: "main-toast" });
            return;
        }

        const pn = tryParsePhone(raw);

        if (!pn) {
            toast.error(t("login.errors.mobileInvalid"), { containerId: "main-toast" });
            return;
        }

        sendingOtpRef.current = true;
        setSendingOtp(true);

        try {
            const payload = {
                countryCode: `+${pn.countryCallingCode}`,
                phone: pn.nationalNumber,
            };

            const res = await otpReq(payload);
            if (res.ok) {
                setOtpCode(res?.data);
                toast.success(t("login.toasts.otpSent"), { containerId: "main-toast" });
                if (window.location.hostname !== "rayabot.ir") {
                    alert(t("login.toasts.otpSentAlert", { code: res.data }));
                }
                setOtpTimer(90);
                setTimeout(() => tryWebOtp(), 900);
            }
            if (!res.ok) {
                setOtpTimer(res?.error?.remainingSeconds);
                toast.error(res?.error?.message);
            }
        } catch (err) {
            console.log(err);
        } finally {
            sendingOtpRef.current = false;
            setSendingOtp(false);
        }
    };

    const onSubmit = async (data) => {
        setLoading(true);

        try {
            let payload;
            let res;
            if (activeTab === "password") {
                payload = { username: data.username, password: data.password };
                res = await loginUser(payload);
            } else {
                const raw = data.mobile;
                const withPlus = raw.startsWith("+") ? raw : `+${raw}`;
                const pn = parsePhoneNumberFromString(withPlus);

                payload = {
                    otp: data.otp,
                    countryCode: pn ? `+${pn.countryCallingCode}` : "",
                    phone: pn ? pn.nationalNumber : data.mobile,
                    purpose: isForgetPassword ? "forgot_password" : null,
                };
                res = await otpVerify(payload);
            }
            if (res.ok) {
                dispatch(
                    login({
                        accessToken: res?.data?.accessToken,
                        refreshToken: res?.data?.refreshToken,
                    })
                );
                const status = res?.data?.registerStatus;

                if (status === "INCOMPLETE_REGISTER") {
                    toast.success(t("login.toasts.completeRegistration"), { containerId: "main-toast" });
                    navigate("/user/authorization");
                } else if (status === "RESET_PASSWORD") {
                    navigate("/forgotPassword", {
                        state: {
                            mode: "forgotPassword",
                        },
                    });
                } else if (status === "COMPLETE") {
                    if (isRegisterPage) {
                        toast.error(t("login.toasts.alreadyRegistered"), { containerId: "main-toast" });
                        navigate("/login");
                    } else {
                        toast.success(t("login.toasts.loginSuccess"), { containerId: "main-toast" });
                        navigate("/dashboard");
                    }
                } else if (activeTab === "password") {
                    toast.success(t("login.toasts.loginSuccess"), { containerId: "main-toast" });
                    navigate("/dashboard");
                }

                reset(
                    activeTab === "password"
                        ? { ...passwordTabValues, mobile: "+98" }
                        : { ...otpTabValues, mobile: "+98" }
                );

                setAutoOtpUsed(false);
            } else {
                toast.error(res.data?.message || t("login.errors.loginFailed"), { containerId: "main-toast" });
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const showError = (fieldName) => {
        const fieldErr = errors?.[fieldName];
        if (!fieldErr) return false;

        const serverErr = fieldErr?.type === "server";
        const val = String(getValues(fieldName) ?? "").trim();
        const touched = touchedFields?.[fieldName];
        if (fieldName === "mobile") {
            if (serverErr) return fieldErr.message;
            if (isSubmitted) return fieldErr.message;
            if (touched) return fieldErr.message;
            if (val !== "") return fieldErr.message;
            return false;
        }

        return serverErr || touched || isSubmitted ? fieldErr.message : false;
    };

    useEffect(() => {
        let runs = [0, 150, 400];
        const timers = runs.map((delay) =>
            setTimeout(() => {
                const names = ["username", "password", "mobile", "otp"];
                let anySet = false;
                names.forEach((n) => {
                    const el = document.querySelector(`input[name="${n}"]`);
                    if (el) {
                        const val = (el.value || "").trim();
                        if (val) {
                            setValue(n, val, { shouldValidate: false, shouldDirty: true });
                            anySet = true;
                        }
                    }
                });
                if (anySet) {
                    setTimeout(() => {
                        trigger();
                    }, 60);
                }
            }, delay)
        );

        return () => timers.forEach((id) => clearTimeout(id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTabChange = (tab) => {
        const current = getValues();
        if (tab === "password") {
            setOtpTabValues({ mobile: current.mobile || "", otp: current.otp || "" });
            setValue("username", passwordTabValues.username || "", { shouldValidate: false, shouldDirty: false });
            setValue("password", passwordTabValues.password || "", { shouldValidate: false, shouldDirty: false });
            clearErrors(["mobile", "otp"]);
        } else {
            setPasswordTabValues({ username: current.username || "", password: current.password || "" });
            setValue("mobile", otpTabValues.mobile || "", { shouldValidate: false, shouldDirty: false });
            setValue("otp", otpTabValues.otp || "", { shouldValidate: false, shouldDirty: false });
            clearErrors(["username", "password"]);
            if (otpTabValues.mobile) setFocus("otp");
        }
        setActiveTab(tab);
        setTimeout(() => trigger(), 50);
    };

    const valsNow = getValues();
    let phoneValidNow = false;
    try {
        if (valsNow.mobile) {
            const withPlusNow = valsNow.mobile.startsWith("+") ? valsNow.mobile : `+${valsNow.mobile}`;
            const pnNow = parsePhoneNumberFromString(withPlusNow);
            phoneValidNow = !!(pnNow && pnNow.isValid());
        }
    } catch {
        phoneValidNow = false;
    }

    const canSubmit =
        !loading &&
        (activeTab === "password"
            ? !!(valsNow.username?.trim() && valsNow.password?.trim()) && !errors.username && !errors.password
            : phoneValidNow && (valsNow.otp || "").length === 5 && !errors.mobile && !errors.otp);

    const forgetHandler = () => {
        setActiveTab("otp");
        setIsForgetPassword(true);
    };

    const showTabs = !(isRegisterPage || isForgetPassword);

    const pageTitle = isRegisterPage
        ? t("login.pageTitles.register")
        : isForgetPassword
            ? t("login.pageTitles.forgotPassword")
            : t("login.pageTitles.login");

    const submitLabel = isForgetPassword
        ? t("login.buttons.send")
        : isRegisterPage
            ? t("login.buttons.register")
            : t("login.buttons.login");

    const loadingNode = (
        <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("login.buttons.sending")}
        </>
    );

    return (
        <section
            dir="rtl"
            className="relative min-h-screen w-full overflow-hidden flex items-center justify-center px-4 py-8 sm:py-12"
        >
            <style>{loginStyles}</style>
            <ToastContainer rtl position="bottom-center" containerId="main-toast" newestOnTop autoClose={5000} />

            <AnimatedBackground />

            {/* Glass shell */}
            <div
                className="raya-card-in relative z-10 w-full max-w-5xl grid md:grid-cols-[1.05fr_1fr] overflow-hidden
                           rounded-[1.75rem] sm:rounded-[2rem]
                           border border-white/70 bg-white/55 backdrop-blur-2xl
                           shadow-[0_30px_80px_-24px_rgba(15,46,46,.45)]
                           dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[0_30px_80px_-24px_rgba(0,0,0,.7)]"
            >
                {/* Brand panel */}
                <div
                    className="relative overflow-hidden flex flex-col items-center justify-center text-center
                               px-6 pt-8 pb-7 md:p-12
                               bg-gradient-to-br from-[#1c4747] via-[#17605d] to-[#0d2f2f] text-white"
                >
                    <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full
                                                       border border-[#f2c14e]/25 raya-ring-b" />
                    <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -right-20 h-80 w-80 rounded-full
                                                       border border-white/10" />

                    <div className="relative h-[104px] w-[104px] md:h-36 md:w-36 flex items-center justify-center">
                        <div
                            aria-hidden="true"
                            className="raya-ring-a absolute inset-0 rounded-full"
                            style={{
                                background:
                                    "conic-gradient(from 0deg, #f2c14e, rgba(242,193,78,0) 35%, rgba(255,255,255,.7) 55%, rgba(242,193,78,0) 75%, #f2c14e)",
                                WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))",
                                mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px))",
                            }}
                        />
                        <div className="h-[86px] w-[86px] md:h-[120px] md:w-[120px] rounded-full bg-white/95 shadow-xl
                                        flex items-center justify-center ring-4 ring-white/10">
                            <img src={logo_raya_EN} alt="Raya" className="object-contain w-[60px] md:w-[84px] block" />
                        </div>
                    </div>

                    <h2
                        className="raya-title mt-5 md:mt-8 text-2xl md:text-[32px] leading-tight font-extrabold
                                   bg-gradient-to-r from-white via-[#ffe7a3] to-white bg-clip-text text-transparent"
                    >
                        {t("login.welcomeTitle")}
                    </h2>
                    <p className="mt-2 text-sm md:text-base text-teal-50/80 max-w-xs">
                        {t("login.welcomeSubtitle")}
                    </p>

                    <ul className="hidden md:flex flex-col gap-3 mt-10 text-sm text-teal-50/85 text-right">
                        <li className="flex items-center gap-3">
                            <span className="h-9 w-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
                                <Smartphone className="w-4 h-4 text-[#f2c14e]" />
                            </span>
                            {t("login.features.phoneLogin")}
                        </li>
                        <li className="flex items-center gap-3">
                            <span className="h-9 w-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
                                <KeyRound className="w-4 h-4 text-[#f2c14e]" />
                            </span>
                            {t("login.features.passwordOrOtp")}
                        </li>
                        <li className="flex items-center gap-3">
                            <span className="h-9 w-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
                                <ShieldCheck className="w-4 h-4 text-[#f2c14e]" />
                            </span>
                            {t("login.features.secureAccount")}
                        </li>
                    </ul>
                </div>

                {/* Form panel */}
                <div className="p-5 sm:p-8 md:p-12 flex flex-col justify-center">
                    <h1 className="text-xl sm:text-2xl font-bold mb-5 text-[#1c4747] dark:text-white">
                        {pageTitle}
                    </h1>

                    {/* Tabs */}
                    {showTabs && (
                        <div
                            role="tablist"
                            className="relative flex p-1 mb-6 rounded-2xl
                                       bg-[#1c4747]/[0.07] border border-[#1c4747]/10
                                       dark:bg-white/[0.06] dark:border-white/10"
                        >
                            <div
                                aria-hidden="true"
                                className="absolute top-1 bottom-1 right-1 w-[calc(50%-4px)] rounded-xl
                                           bg-gradient-to-r from-[#e0ad3a] to-[#f6d070]
                                           shadow-[0_6px_16px_-6px_rgba(224,173,58,.9)]
                                           transition-transform duration-300 ease-out"
                                style={{ transform: activeTab === "password" ? "translateX(0)" : "translateX(-100%)" }}
                            />
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === "password"}
                                onClick={() => { handleTabChange("password"); setIsForgetPassword(false); }}
                                className={`relative z-10 w-1/2 py-2.5 text-[13px] sm:text-sm font-semibold rounded-xl transition-colors duration-300
                                    ${activeTab === "password" ? "text-[#0d2f2f]" : "text-[#1c4747]/70 dark:text-white/60"}`}
                            >
                                {t("login.tabs.password")}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === "otp"}
                                onClick={() => handleTabChange("otp")}
                                className={`relative z-10 w-1/2 py-2.5 text-[13px] sm:text-sm font-semibold rounded-xl transition-colors duration-300
                                    ${activeTab === "otp" ? "text-[#0d2f2f]" : "text-[#1c4747]/70 dark:text-white/60"}`}
                            >
                                {t("login.tabs.otp")}
                            </button>
                        </div>
                    )}

                    <div className="min-h-[300px]">
                        {isRegisterPage || activeTab === "otp" ? (
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" autoComplete="tel">
                                <div>
                                    <label className={labelCls}>{t("login.labels.mobile")}</label>
                                    <PhoneInput
                                        country={"ir"}
                                        value={watchMobile}
                                        onChange={(value) =>
                                            setValue("mobile", value, {
                                                shouldValidate: hasInteracted,
                                                shouldDirty: true,
                                            })
                                        }
                                        onBlur={() => {
                                            setHasInteracted(true);
                                            trigger("mobile");
                                        }}
                                        containerClass="raya-phone"
                                        disableCountryCode={false}
                                        countryCodeEditable={false}
                                        enableSearch={true}
                                        disableSearchIcon={true}
                                        excludeCountries={["tr", "af", "us"]}
                                        inputProps={{
                                            style: { direction: "ltr", textAlign: "left" },
                                            name: "mobile",
                                            inputMode: "tel",
                                            autoComplete: "tel",
                                        }}
                                    />

                                    {hasInteracted && showError("mobile") && (
                                        <p className={errCls}>{showError("mobile")}</p>
                                    )}
                                </div>

                                <div>
                                    <label className={labelCls}>{t("login.labels.otp")}</label>
                                    <div className="relative">
                                        <input
                                            name="otp"
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            {...register("otp")}
                                            onChange={(e) => {
                                                const v = e.target.value.replace(/\D/g, "").slice(0, 5);
                                                setValue("otp", v, { shouldValidate: true, shouldDirty: true });
                                            }}
                                            className={`${inputCls} pl-[8.5rem] tracking-[0.3em]`}
                                            placeholder={t("login.placeholders.otp")}
                                        />

                                        <div className="absolute inset-y-0 left-2 flex items-center">
                                            {otpTimer > 0 ? (
                                                <div
                                                    className={`${pillBtnClasses} min-w-[84px] justify-center text-white
                                                                bg-gradient-to-r from-[#1c4747] to-[#2a7a76]
                                                                dark:from-[#e0ad3a] dark:to-[#f6d070] dark:text-[#0d2f2f]`}
                                                >
                                                    {otpTimer}
                                                    <Hourglass className="w-4 h-4" />
                                                </div>
                                            ) : (
                                                <button
                                                    disabled={sendingOtp || otpTimer > 0}
                                                    type="button"
                                                    onClick={async () => {
                                                        await trigger("mobile");
                                                        const vals = getValues();
                                                        const raw = vals.mobile;
                                                        if (!raw) {
                                                            toast.error(t("login.errors.mobileInvalid"), { containerId: "main-toast" });
                                                            return;
                                                        }
                                                        const withPlus = raw.startsWith("+") ? raw : `+${raw}`;
                                                        const pn = parsePhoneNumberFromString(withPlus);
                                                        if (!pn || !pn.isValid()) {
                                                            toast.error(t("login.errors.mobileInvalid"), { containerId: "main-toast" });
                                                            return;
                                                        }
                                                        handleSendOtp();
                                                    }}
                                                    className={`${pillBtnClasses} text-[#0d2f2f]
                                                                bg-gradient-to-r from-[#e0ad3a] to-[#f6d070]
                                                                hover:from-[#d29d28] hover:to-[#efc55c]
                                                                active:scale-95 transition-all disabled:opacity-70
                                                                focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1c4747]`}
                                                >
                                                    {sendingOtp ? t("login.buttons.sending") : t("login.buttons.getCode")}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {showError("otp") && <p className={errCls}>{showError("otp")}</p>}
                                </div>

                                <button type="submit" className={ctaCls}>
                                    {canSubmit && !loading && <span className="raya-shine" aria-hidden="true" />}
                                    <span className="relative flex items-center gap-2">
                                        {loading ? loadingNode : submitLabel}
                                    </span>
                                </button>

                                {isForgetPassword ? (
                                    <button
                                        type="button"
                                        onClick={() => setIsForgetPassword(false)}
                                        className={linkBtnCls}
                                    >
                                        {t("login.buttons.backToLogin")}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            (window.location.href = isRegisterPage ? "/login" : "/register")
                                        }
                                        className={linkBtnCls}
                                    >
                                        {isRegisterPage
                                            ? t("login.buttons.alreadyRegistered")
                                            : t("login.buttons.notRegistered")}
                                    </button>
                                )}
                            </form>
                        ) : (
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" autoComplete="on">
                                <div>
                                    <label className={labelCls}>{t("login.labels.username")}</label>
                                    <input
                                        name="username"
                                        type="text"
                                        autoComplete="username"
                                        placeholder={t("login.placeholders.username")}
                                        {...register("username")}
                                        className={inputCls}
                                    />
                                    {showError("username") && <p className={errCls}>{showError("username")}</p>}
                                </div>

                                <div>
                                    <label className={labelCls}>{t("login.labels.password")}</label>
                                    <div className="relative">
                                        <input
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder={t("login.placeholders.password")}
                                            autoComplete="current-password"
                                            {...register("password")}
                                            className={`${inputCls} pl-12`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            aria-label={
                                                showPassword
                                                    ? t("login.buttons.hidePassword")
                                                    : t("login.buttons.showPassword")
                                            }
                                            className="absolute inset-y-0 left-2 my-auto h-9 w-9 flex items-center justify-center rounded-lg
                                                       text-[#1c4747]/60 hover:text-[#1c4747] hover:bg-[#1c4747]/10
                                                       dark:text-white/50 dark:hover:text-[#f2c14e] dark:hover:bg-white/10 transition-colors"
                                            tabIndex={-1}
                                        >
                                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                    {showError("password") && <p className={errCls}>{showError("password")}</p>}
                                </div>

                                <button type="submit" className={ctaCls}>
                                    {canSubmit && !loading && <span className="raya-shine" aria-hidden="true" />}
                                    <span className="relative flex items-center gap-2">
                                        {loading ? loadingNode : t("login.buttons.login")}
                                    </span>
                                </button>

                                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 pt-1">
                                    <button
                                        type="button"
                                        onClick={() => (window.location.href = "/register")}
                                        className={linkBtnCls}
                                    >
                                        {t("login.buttons.notRegistered")}
                                    </button>

                                    {!isForgetPassword ? (
                                        <button type="button" onClick={() => forgetHandler()} className={linkBtnCls}>
                                            {t("login.buttons.forgotPassword")}
                                        </button>
                                    ) : null}
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}